<script setup lang="ts">
import { onMounted, computed, onUnmounted } from "vue";
import Article from "~/components/card/Article.vue";
import { useRoute } from "vue-router";
import { useLazyAsyncData } from "#app";
import { ContentTypeEnum } from "~/types/enum/ContentTypeEnum";
import { dateDayFormatter } from "~/utils/formatters/date.formatter";
import { clearAdsenseBlocks } from "~/utils/adsense";
import { useProductReviewDetailPageStore } from "~/stores/shop/review/useProductReviewDetailPageStore";

const productReviewDetailPageStore = useProductReviewDetailPageStore();
const slug = String(useRoute().params?.slug);

await useLazyAsyncData('productReviewDetailPageStore', async () => productReviewDetailPageStore.fetch(slug));
if (typeof productReviewDetailPageStore.error === "object") {
    throw productReviewDetailPageStore.error;
}

onMounted(() => {
    window.scrollTo(0, 0);
});

onUnmounted(() => {
    clearAdsenseBlocks();
    productReviewDetailPageStore.clear();
});
</script>

<template>
    <article v-if="productReviewDetailPageStore.title !== undefined" class="max-w-2xl px-6 mx-auto">
        <div class="w-full mx-auto space-y-4">
            <h1 class="text-4xl font-bold tracking-tight md:text-5xl xl:text-6xl">{{ productReviewDetailPageStore.title }}</h1>
            <div>
                <nuxt-link to="/reviews" class="font-semibold text-indigo-800 mr-1">
                    Reviews <span aria-hidden="true">&rarr;</span>
                </nuxt-link>
                <nuxt-link :to="productReviewDetailPageStore.category.url" class="font-semibold text-indigo-800">
                    {{ productReviewDetailPageStore.category.title }}
                </nuxt-link>
            </div>
            <p class="text-sm">
                <time :datetime="productReviewDetailPageStore.date">{{ dateDayFormatter(productReviewDetailPageStore.date) }}</time>
            </p>
        </div>
    </article>
    <ClientOnly>
        <section class="flex container justify-center">
            <AdsenseSmallHorizontal/>
        </section>
    </ClientOnly>

    <ClientOnly>
        <section class="container">
            <Swiper
                class="products"
                :modules="[SwiperNavigation, SwiperEffectCreative]"
                navigation
                center-insufficient-slides
                :space-between="15"
                :slides-per-view="2"
                :loop="false"
                :breakpoints="{
                    '1000': {
                        slidesPerView: 3,
                    },
                    '1280': {
                        slidesPerView: 5,
                    },
                }"
            >
                <SwiperSlide
                    v-for="(productGroup, index) in productReviewDetailPageStore.products"
                    :key="index" :class="{'border-gray-100': !productGroup.highlight, 'border-yellow-400': productGroup.highlight}"
                    class="border-2 p-4 rounded-lg space-y-2 md:space-y-4 flex-col justify-between"
                >
                    <CardShopProductMediumPreview
                        :title="productGroup.product.title"
                        :picture-url="productGroup.product.images[0]"
                        :price="productGroup.product.price"
                        :old-price="productGroup.product.oldPrice"
                        :detail-url="productGroup.product.url"
                        :buy-url="productGroup.product.buyUrl"
                    />
                </SwiperSlide>
            </Swiper>
        </section>
    </ClientOnly>

    <template v-if="productReviewDetailPageStore.content !== undefined && productReviewDetailPageStore.content.length"
              v-for="content in productReviewDetailPageStore.content"
    >
        <ClientOnly>
            <section class="container flex justify-center">
                <AdsenseSmallHorizontal />
            </section>
        </ClientOnly>
        <ContentText v-if="content.type === ContentTypeEnum.Text" :content="content"/>
        <ContentRandomRecipesByCategory v-if="content.type === ContentTypeEnum.RandomRecipesByCategory" :content="content"/>
        <ClientOnly v-if="content.type === ContentTypeEnum.RandomRecipesByCategory">
            <section class="container flex justify-center">
                <AdsenseMedia />
            </section>
        </ClientOnly>
        <ContentImageText v-if="content.type === ContentTypeEnum.ImageText" :content="content" :title="productReviewDetailPageStore.title"/>
    </template>

    <template
        v-if="productReviewDetailPageStore.title !== undefined"
        v-for="(productGroup, index) in productReviewDetailPageStore.products"
        :key="index"
    >
        <CardShopProductInfo
            class="sm:container"
            :title="productGroup.product.title"
            :sub-title="productGroup.product.subTitle"
            :category="productGroup.product.category"
            :old-price="productGroup.product.oldPrice"
            :price="productGroup.product.price"
            :buy-url="productGroup.product.buyUrl"
            :source="productGroup.product.source"
            :images="productGroup.product.images"
            :ratings="productGroup.product.ratings"
            :total-rating="productGroup.product.totalRating"
            in-review-page
            :highlight="productGroup.highlight"
        />

        <template v-if="productGroup.product.content !== undefined && productGroup.product.content.length"
                  v-for="content in productGroup.product.content"
        >
            <ClientOnly>
                <section class="container flex justify-center">
                    <AdsenseSmallHorizontal />
                </section>
            </ClientOnly>
            <ContentText v-if="content.type === ContentTypeEnum.Text" :content="content"/>
            <ContentRandomRecipesByCategory v-if="content.type === ContentTypeEnum.RandomRecipesByCategory" :content="content"/>
            <ContentImageText v-if="content.type === ContentTypeEnum.ImageText" :content="content" :title="productGroup.product.title"/>
        </template>
        <ClientOnly>
            <section class="flex container justify-center">
                <AdsenseMedia/>
            </section>
        </ClientOnly>
    </template>
</template>